/**
 * Field to Sub-Step Mapper Utility
 *
 * Resolves where a field lives in the application workflow, so the Application
 * Status page can deep-link the applicant to the sub-step holding a field that
 * needs updating.
 *
 * FORM_FIELD_LOCATIONS owns every location, keyed by workflow form field name
 * (`config/workflow.ts`). RETURN_FIELD_PATH_TO_FORM_FIELD aliases the dotted
 * `applicationData` paths emitted by the Approvals side
 * (`pages/approvals/applicationDataMapper.ts`) onto those form field names.
 *
 * This file is the single translation point between the two vocabularies;
 * nothing else imports across the Approvals/Application boundary.
 */

import { PrimaryStep, SubStep } from '../config/steps';

/**
 * Mapping of field names to their step and sub-step locations
 */
export interface FieldLocation {
  /**
   * The primary step ID where the field is located
   */
  stepId: PrimaryStep;
  /**
   * The sub-step ID where the field is located
   */
  subStepId: SubStep;
  /**
   * Human-readable description of where the field is located (for debugging)
   */
  description: string;
}

/**
 * Aliases a dotted `returnFeedback.fieldPath` (emitted by `fieldPath` in
 * pages/approvals/applicationDataMapper.ts) onto its form field name.
 *
 * Only paths whose last segment can't resolve on its own need an alias — either
 * it collides with another section's field (`workAddress.city` vs the Personal
 * Details `city`) or it diverges from the form name (`cmsRegionFacility` vs
 * `cmsRegion`). Every other path resolves by its last segment.
 */
const RETURN_FIELD_PATH_TO_FORM_FIELD: Record<string, string> = {
  'citizenshipDetails.countryOfCitizenship': 'citizenshipCountry',
  'citizenshipDetails.passport.expirationDate': 'passportExpirationDate',
  'citizenshipDetails.passport.issuanceCountry': 'passportIssuanceCountry',
  'citizenshipDetails.passport.issueDate': 'passportIssueDate',
  'citizenshipDetails.passport.number': 'passportNumber',
  'citizenshipDetails.passport.type': 'passportType',
  'citizenshipDetails.yearsInUSPast5Years': 'yearsInUS',
  'contractDetails.accessJustification': 'requestJustification',
  'contractDetails.cmsRegionFacility': 'cmsRegion',
  'contractDetails.workAddress.city': 'workAddressCity',
  'contractDetails.workAddress.state': 'workAddressState',
  'contractDetails.workAddress.streetLine1': 'workAddressLine1',
  'contractDetails.workAddress.streetLine2': 'workAddressLine2',
  'contractDetails.workAddress.zipCode': 'workAddressZipCode',
  // Keyed on the bare leaf so it covers `citizenshipDetails.isUsCitizen` too.
  isUsCitizen: 'isUSCitizen',
  'personalDetails.otherNamesUsed': 'otherNames',
  'personalDetails.personalAddress.streetLine1': 'addressLine1',
  'personalDetails.personalAddress.streetLine2': 'addressLine2',
  'personalDetails.personalPhoneNumber': 'phoneNumber',
};

/**
 * Application workflow vocabulary: form field names, mirroring the `defaultData`
 * keys in config/workflow.ts. Names are globally unique across the workflow, so
 * a flat map is enough.
 *
 * Also serves VAS-originated feedback, which sends bare field names rather than
 * dotted paths.
 *
 * When adding a new field to the workflow, add its mapping here.
 */
export const FORM_FIELD_LOCATIONS: Record<string, FieldLocation> = {
  // Personal Details - Address Information
  addressLine1: {
    description: 'Personal Details > Address Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.ADDRESS_INFO,
  },
  addressLine2: {
    description: 'Personal Details > Address Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.ADDRESS_INFO,
  },
  // Citizenship Details - Immigration Information
  alienRegistrationNumber: {
    description: 'Citizenship Details > Immigration Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.IMMIGRATION_DOCUMENT,
  },
  // Citizenship Details - Birth Information
  birthCity: {
    description: 'Citizenship Details > Birth Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.BIRTH_INFORMATION,
  },
  birthCountry: {
    description: 'Citizenship Details > Birth Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.BIRTH_INFORMATION,
  },
  birthCountryCode: {
    description: 'Citizenship Details > Birth Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.BIRTH_INFORMATION,
  },
  birthState: {
    description: 'Citizenship Details > Birth Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.BIRTH_INFORMATION,
  },
  // Citizenship Details - Residency Information
  citizenshipCountry: {
    description: 'Citizenship Details > Residency Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.RESIDENCY_INFORMATION,
  },
  citizenshipCountryCode: {
    description: 'Citizenship Details > Residency Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.RESIDENCY_INFORMATION,
  },
  // Personal Details - Address Information
  city: {
    description: 'Personal Details > Address Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.ADDRESS_INFO,
  },
  // Contract Details - Contract Information
  cmsRegion: {
    description: 'Contract Details > Contract Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.CONTRACT_INFO,
  },
  company: {
    description: 'Contract Details > Contract Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.CONTRACT_INFO,
  },
  contractNumber: {
    description: 'Contract Details > Contract Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.CONTRACT_INFO,
  },
  // Personal Details - Basic Information
  dateOfBirth: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  // Citizenship Details - Immigration Information
  documentType: {
    description: 'Citizenship Details > Immigration Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.IMMIGRATION_DOCUMENT,
  },
  // Citizenship Details - U.S. Entry Information
  entryVisaType: {
    description: 'Citizenship Details > U.S. Entry Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.US_ENTRY_INFORMATION,
  },
  // Citizenship Details - U.S. Entry Information
  firstEntryDate: {
    description: 'Citizenship Details > U.S. Entry Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.US_ENTRY_INFORMATION,
  },
  // Personal Details - Basic Information
  firstName: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  hasMiddleName: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  // Citizenship Details - Immigration Information
  immigrationDate: {
    description: 'Citizenship Details > Immigration Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.IMMIGRATION_DOCUMENT,
  },
  // Citizenship Details - Residency Information
  isPermanentResident: {
    description: 'Citizenship Details > Residency Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.RESIDENCY_INFORMATION,
  },
  // Citizenship Details - Citizenship Status
  isUSCitizen: {
    description: 'Citizenship Details > Citizenship Status',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.CITIZENSHIP_STATUS,
  },
  // Contract Details - Contract Information
  laborCategory: {
    description: 'Contract Details > Contract Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.CONTRACT_INFO,
  },
  // Personal Details - Basic Information
  lastName: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  // Document Upload - Upload Documents
  legalDocuments: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  // Personal Details - Basic Information
  middleName: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  // Document Upload - Upload Documents
  otherDocuments: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  // Personal Details - Basic Information
  otherNames: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  // Document Upload - Upload Documents
  passport: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  // Citizenship Details - Passport Information
  passportExpirationDate: {
    description: 'Citizenship Details > Passport Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.PASSPORT_INFORMATION,
  },
  passportIssuanceCountry: {
    description: 'Citizenship Details > Passport Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.PASSPORT_INFORMATION,
  },
  passportIssuanceCountryCode: {
    description: 'Citizenship Details > Passport Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.PASSPORT_INFORMATION,
  },
  passportIssueDate: {
    description: 'Citizenship Details > Passport Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.PASSPORT_INFORMATION,
  },
  passportNumber: {
    description: 'Citizenship Details > Passport Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.PASSPORT_INFORMATION,
  },
  passportType: {
    description: 'Citizenship Details > Passport Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.PASSPORT_INFORMATION,
  },
  // Personal Details - Contact Information
  phoneNumber: {
    description: 'Personal Details > Contact Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.CONTACT_INFO,
  },
  // Document Upload - Upload Documents
  pivTrainingCertificate: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  // Personal Details - Contact Information
  registrationEmail: {
    description: 'Personal Details > Contact Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.CONTACT_INFO,
  },
  // Contract Details - Contract Information
  requestJustification: {
    description: 'Contract Details > Contract Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.CONTRACT_INFO,
  },
  // Document Upload - Upload Documents
  residencyDocument: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  residencySupportingDocuments: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  resume: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  // Citizenship Details - Birth Information
  sex: {
    description: 'Citizenship Details > Birth Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.BIRTH_INFORMATION,
  },
  // Personal Details - Basic Information
  ssn: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  ssnConfirm: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  // Personal Details - Address Information
  state: {
    description: 'Personal Details > Address Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.ADDRESS_INFO,
  },
  // Personal Details - Basic Information
  suffix: {
    description: 'Personal Details > Basic Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.BASIC_INFO,
  },
  // Personal Details - Contact Information
  userCategory: {
    description: 'Personal Details > Contact Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.CONTACT_INFO,
  },
  // Document Upload - Upload Documents
  usVisaDocument: {
    description: 'Document Upload > Upload Documents',
    stepId: PrimaryStep.DOCUMENT_UPLOAD,
    subStepId: SubStep.UPLOAD_DOCUMENTS,
  },
  // Citizenship Details - Immigration Information
  visaNumber: {
    description: 'Citizenship Details > Immigration Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.IMMIGRATION_DOCUMENT,
  },
  // Contract Details - Work Contact Information
  workAddressCity: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  workAddressLine1: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  workAddressLine2: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  workAddressState: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  workAddressZipCode: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  workEmail: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  workExtension: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  // Personal Details - Address Information
  workFromHome: {
    description: 'Personal Details > Address Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.ADDRESS_INFO,
  },
  // Citizenship Details - Immigration Information
  workPermitNumber: {
    description: 'Citizenship Details > Immigration Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.IMMIGRATION_DOCUMENT,
  },
  // Contract Details - Work Contact Information
  workPhoneNumber: {
    description: 'Contract Details > Work Contact Information',
    stepId: PrimaryStep.CONTRACT_DETAILS,
    subStepId: SubStep.WORK_CONTACT_INFO,
  },
  // Citizenship Details - U.S. Entry Information
  yearsInUS: {
    description: 'Citizenship Details > U.S. Entry Information',
    stepId: PrimaryStep.CITIZENSHIP_DETAILS,
    subStepId: SubStep.US_ENTRY_INFORMATION,
  },
  // Personal Details - Address Information
  zipCode: {
    description: 'Personal Details > Address Information',
    stepId: PrimaryStep.PERSONAL_DETAILS,
    subStepId: SubStep.ADDRESS_INFO,
  },
};

/**
 * Gets the step and sub-step location for a returnFeedback `fieldPath` or a bare
 * form field name.
 *
 * Resolves the input to a form field name first — by full-path alias, then by
 * leaf alias, then the leaf itself (`personalDetails.dateOfBirth` ->
 * `dateOfBirth`) — and looks that up in FORM_FIELD_LOCATIONS.
 *
 * @param input - A dotted field path or a bare field name
 * @returns The field location if found, undefined otherwise
 *
 * @example
 * ```typescript
 * const location = getFieldLocation('personalDetails.dateOfBirth');
 * if (location) {
 *   console.log(`Field is in ${location.description}`);
 *   // Navigate to location.stepId and location.subStepId
 * }
 * ```
 */
export function getFieldLocation(input: string): FieldLocation | undefined {
  const leaf = input.split('.').pop() ?? input;
  const formField = RETURN_FIELD_PATH_TO_FORM_FIELD[input] ?? RETURN_FIELD_PATH_TO_FORM_FIELD[leaf] ?? leaf;

  return FORM_FIELD_LOCATIONS[formField];
}

/**
 * Gets the step and sub-step indices for a given field name.
 * This is useful for navigating to the correct step in the workflow.
 *
 * @param fieldName - The name of the field to look up
 * @param workflowSteps - The workflow configuration steps array
 * @returns An object with stepIndex and subStepIndex, or undefined if not found
 *
 * @example
 * ```typescript
 * const indices = getFieldIndices('email', workflowConfig.steps);
 * if (indices) {
 *   goToStep(indices.stepIndex, indices.subStepIndex);
 * }
 * ```
 */
export function getFieldIndices(
  fieldName: string,
  workflowSteps: Array<{ id: string; subSteps: Array<{ id: string }> }>,
): { stepIndex: number; subStepIndex: number } | undefined {
  const location = getFieldLocation(fieldName);
  if (!location) {
    console.warn(`No location mapping found for field: ${fieldName}`);

    return undefined;
  }

  // Find the step index
  const stepIndex = workflowSteps.findIndex((step) => step.id === location.stepId);
  if (stepIndex === -1) {
    console.warn(`Step not found for field ${fieldName}: ${location.stepId}`);

    return undefined;
  }

  // Find the sub-step index within that step
  const subStepIndex = workflowSteps[stepIndex].subSteps.findIndex((subStep) => subStep.id === location.subStepId);
  if (subStepIndex === -1) {
    console.warn(`Sub-step not found for field ${fieldName}: ${location.subStepId}`);

    return undefined;
  }

  return { stepIndex, subStepIndex };
}

/**
 * Validates that a list of field names/paths all have mappings.
 * Useful for debugging and ensuring the maps are kept up to date.
 *
 * @param fieldNames - Array of field names or paths to validate
 * @returns Object with valid and invalid field names
 */
export function validateFieldMappings(fieldNames: string[]): {
  valid: string[];
  invalid: string[];
} {
  const valid: string[] = [];
  const invalid: string[] = [];

  fieldNames.forEach((fieldName) => {
    if (getFieldLocation(fieldName)) {
      valid.push(fieldName);
    } else {
      invalid.push(fieldName);
    }
  });

  return { invalid, valid };
}
